import { useEffect, useRef, useState } from 'react';
import { Save, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ComponentSelect from '@/components/ComponentSelect';
import { useUnsaved } from '@/components/UnsavedGuard';
import api, { apiError } from '@/lib/api';
import { INPUT_FIELDS, blankPricing, calculatePricing, pricingPayload, validatePricing } from '@/lib/b2bPricing';
import { PricingResults } from './PricingResults';

export const PricingForm = ({ editor, products, onClose, onSaved }) => {
  const [input, setInput] = useState(() => editor.row ? { ...blankPricing(), ...editor.row } : blankPricing());
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const { setDirty: setUnsaved } = useUnsaved();
  const formRef = useRef(null);
  const product = products.find(p => p.id === input.product_id);
  const values = calculatePricing(input, product?.ep_rate_gm);
  const options = [...products].sort((a, b) => Number(!!a.source_error) - Number(!!b.source_error) || a.name.localeCompare(b.name))
    .map(p => ({ value: p.id, label: `${p.sku_id} · ${p.name}${p.source_error ? ' · Unavailable' : ''}`, level: 'L2' }));

  useEffect(() => {
    setUnsaved(dirty ? 'Discard unsaved B2B pricing changes?' : null);
    const guard = e => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', guard);
    return () => { setUnsaved(null); window.removeEventListener('beforeunload', guard); };
  }, [dirty, setUnsaved]);

  const change = (key, value) => {
    setInput(p => ({ ...p, [key]: value })); setDirty(true);
    setErrors(p => ({ ...p, [key]: undefined })); setError('');
  };
  const close = () => { if (!busy && (!dirty || window.confirm('Discard unsaved B2B pricing changes?'))) onClose(); };
  const save = async (addAnother = false) => {
    if (busy) return;
    const issues = validatePricing(input, product);
    setErrors(issues); setError('');
    if (Object.keys(issues).length) {
      formRef.current?.querySelector(`[data-field="${Object.keys(issues)[0]}"] input, [data-field="${Object.keys(issues)[0]}"] button`)?.focus();
      return;
    }
    setBusy(true);
    try {
      const payload = { ...pricingPayload(input), password: editor.password };
      const response = editor.row
        ? await api.put(`/b2b-pricing/${editor.row.id}`, payload)
        : await api.post('/b2b-pricing', payload);
      setDirty(false);
      onSaved(response.data, addAnother);
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  const enter = e => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    e.preventDefault();
    if (!e.repeat) save(true);
  };

  return <Dialog open onOpenChange={value => { if (!value) close(); }}>
    <DialogContent data-testid="b2b-form-dialog" className="b2b-form-dialog rounded-sm" onInteractOutside={e => e.preventDefault()}>
      <DialogHeader>
        <DialogTitle data-testid="b2b-form-title" className="text-lg font-semibold">{editor.row ? 'Edit product pricing' : 'Add product pricing'}</DialogTitle>
        <DialogDescription className="sr-only">B2B pack and portion pricing, exclusive of GST.</DialogDescription>
      </DialogHeader>
      <form ref={formRef} noValidate onSubmit={e => { e.preventDefault(); save(false); }}>
        <div className="grid min-w-0 gap-6 md:grid-cols-[1.1fr_1fr]">
          <section className="min-w-0" aria-labelledby="b2b-raw-title">
            <h2 id="b2b-raw-title" className="mb-3 text-base font-semibold">Raw inputs</h2>
            <div className="mb-3" data-field="product_id">
              <label className="b2b-label">SKU Name <span aria-hidden="true">*</span></label>
              <ComponentSelect testid="b2b-product-select" options={options} value={input.product_id} onChange={v => change('product_id', v)} />
              {errors.product_id && <p data-testid="b2b-error-product_id" role="alert" className="b2b-field-error">{errors.product_id}</p>}
              {product?.source_error && !errors.product_id && <p data-testid="b2b-source-error" role="alert" className="b2b-field-error">{product.source_error}</p>}
              {!products.length && <p data-testid="b2b-no-products" className="b2b-field-error">No L2 products available.</p>}
            </div>
            <div className="mb-4 flex justify-between border-b border-zinc-200 pb-3 text-xs text-zinc-500">
              <span>SKU ID</span><output data-testid="b2b-fetched-sku" className="font-mono text-zinc-800">{product?.sku_id || '—'}</output>
            </div>
            <fieldset disabled={busy} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {INPUT_FIELDS.map(field => <div key={field.key} data-field={field.key} className="min-w-0">
                <label htmlFor={`b2b-input-${field.key}`} className="b2b-label">{field.label}{field.required && <span aria-hidden="true"> *</span>}</label>
                <input id={`b2b-input-${field.key}`} data-testid={`b2b-input-${field.key}`} type={field.type || 'number'} required={field.required}
                  value={input[field.key] ?? ''} onChange={e => change(field.key, e.target.value)} onKeyDown={enter}
                  min={field.min} max={field.max ?? (field.type === 'text' ? undefined : 1e9)} maxLength={field.type === 'text' ? 80 : undefined} step={field.step || 'any'}
                  aria-invalid={!!errors[field.key]} aria-describedby={errors[field.key] ? `b2b-field-error-${field.key}` : undefined}
                  className={`b2b-input ${field.type === 'text' ? '' : 'text-right font-mono'}`} />
                {errors[field.key] && <p id={`b2b-field-error-${field.key}`} data-testid={`b2b-error-${field.key}`} role="alert" className="b2b-field-error">{errors[field.key]}</p>}
              </div>)}
            </fieldset>
          </section>
          <PricingResults values={values} />
        </div>
        {error && <div data-testid="b2b-save-error" role="alert" className="mt-4 border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}
        <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-zinc-200 pt-4">
          <span data-testid="b2b-form-dirty" className="mr-auto text-xs text-amber-700">{dirty ? 'Unsaved changes' : ''}</span>
          <button data-testid="b2b-form-cancel" type="button" className="b2b-button" onClick={close} disabled={busy}>Cancel</button>
          <button data-testid="b2b-save-add-new" type="button" className="b2b-button" onClick={() => save(true)} disabled={busy}><Plus size={14} /> Save & add new</button>
          <button data-testid="b2b-save" type="submit" className="b2b-button b2b-button-primary" disabled={busy}><Save size={14} />{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </DialogContent>
  </Dialog>;
};